import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, ChevronDown, Inbox } from 'lucide-react';
import { API_BASE as API } from '../../config';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import TodoDropdown from '../TodoDropdown';

/**
 * Desktop primary nav links (left side of the bar).
 * Home / Intake / All Entries / Company Entries dropdown / Todo / Org Switcher (Provider).
 */
export default function DesktopNavLinks({
  isActive, hasRole, user, inContext,
  companies, handleCompanySelect,
  visibleOrgs, activeOrgId, setActiveOrgId,
}) {
  const navLink = (path, label) => (
    <Link
      to={path}
      className={`px-4 py-2 rounded text-sm transition-colors ${
        isActive(path) ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
      }`}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {label}
    </Link>
  );

  const showTenantLinks = !user?.is_platform_operator || inContext;

  return (
    <div className="hidden md:flex items-center gap-2">
      {navLink('/', 'Home')}
      {showTenantLinks && hasRole('super_admin', 'admin', 'manager', 'member') && navLink('/intake', 'Intake')}
      {showTenantLinks && navLink('/all-entries', 'All Entries')}
      {showTenantLinks && <InboxNavLink isActive={isActive} />}

      {showTenantLinks && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={`px-4 py-2 rounded text-sm flex items-center gap-1 transition-colors ${
              isActive('/companies') ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
            data-testid="nav-companies"
          >
            Company Entries
            <ChevronDown className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {companies.length > 0 ? (
              companies.map((company) => (
                <DropdownMenuItem key={company.name} onClick={() => handleCompanySelect(company.name)}>
                  {company.name}
                </DropdownMenuItem>
              ))
            ) : (
              <DropdownMenuItem disabled>No companies available</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {showTenantLinks && <TodoDropdown />}

      {/* V2: Org Switcher for Provider accounts */}
      {user?.account_type === 'provider' && visibleOrgs.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="px-3 py-1.5 rounded text-xs flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
            data-testid="org-switcher"
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>{activeOrgId ? visibleOrgs.find(o => o.org_id === activeOrgId)?.name || 'Org' : 'All Orgs'}</span>
            <ChevronDown className="w-3 h-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onClick={() => setActiveOrgId(null)}
              className={!activeOrgId ? 'bg-indigo-50 font-medium' : ''}
              data-testid="org-switch-all"
            >
              All Organizations
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {visibleOrgs.map(org => (
              <DropdownMenuItem
                key={org.org_id}
                onClick={() => setActiveOrgId(org.org_id)}
                className={activeOrgId === org.org_id ? 'bg-indigo-50 font-medium' : ''}
                data-testid={`org-switch-${org.org_id}`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: org.branding_color || '#6366f1' }} />
                  <span className="truncate">{org.name}</span>
                  {!org.data_visible && <span className="text-[10px] text-gray-400 ml-1">(hidden)</span>}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

/**
 * Inbox nav link with a live unread-count badge. Polls /api/inbox/unread-count
 * every 60 seconds AND on demand when the inbox page fires `inbox:seen` (so
 * the badge clears immediately when the user opens the inbox, no waiting for
 * the next poll tick).
 */
function InboxNavLink({ isActive }) {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/inbox/unread-count`);
        if (!cancelled) setUnread(r.data?.unread || 0);
      } catch { /* non-blocking */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    const onSeen = () => fetchCount();
    window.addEventListener('inbox:seen', onSeen);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('inbox:seen', onSeen);
    };
  }, []);

  return (
    <Link
      to="/inbox"
      className={`relative px-4 py-2 rounded text-sm transition-colors inline-flex items-center gap-1.5 ${
        isActive('/inbox') ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
      }`}
      data-testid="nav-inbox"
    >
      <Inbox className="w-4 h-4" />
      Inbox
      {unread > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-blue-600 text-white text-[10px] font-semibold rounded-full"
          data-testid="nav-inbox-unread-badge"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
}
